import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../lib/useAuth'
import { validateEmail, validateLogOp } from '../lib/validate'
import { ArrowLeft, Plus, QrCodeIcon, RefreshCw, Users, Shield, Pencil, Check, X, ChevronDown, ChevronRight, Trash2 } from 'lucide-react'
import ShareQR from '../components/ShareQR'
import setThemeColor from '../lib/themeColor'
import InviteOperator from '../components/InviteOperator'

const TIER_COLOR = {
  'Old Guard':  { badge: '#ff7a18', bg: 'rgba(255,122,24,.12)', text: '#ff7a18' },
  'Member':     { badge: '#4dd6ff', bg: 'rgba(77,214,255,.10)', text: '#4dd6ff' },
  'Contractor': { badge: '#9aa6b0', bg: 'rgba(154,166,176,.08)', text: '#9aa6b0' },
  'Recruit':    { badge: '#5a6470', bg: 'rgba(90,100,112,.08)', text: '#5a6470' },
  'Prospect':   { badge: '#aef0ff', bg: 'rgba(174,240,255,.08)', text: '#aef0ff' },
}

const ROLE_ICON = {
  'Patrol Lead': '✈', 'Rifleman': '✦', 'Medic': '♥',
  'Sniper': '✉', 'Heavy Weapons': '◆', 'Comms Chief': '✎',
  'Comms Chief Backup': '✎', 'DMR': '✉',
}

const STATUS_COLOR = {
  'planning': '#ffb259', 'confirmed': '#4dd6ff',
  'active': '#ff7a18', 'complete': '#9aa6b0',
}

const FIELD_ROLES = ['Patrol Lead','Rifleman','Medic','Sniper','Heavy Weapons','Comms Chief','Comms Chief Backup','DMR']
const DEFAULT_TIERS = ['Old Guard','Member','Contractor','Recruit','Prospect']
const EVENT_STATUSES = ['planning','confirmed','active','complete']

export default function CommandCenter() {
  const { id: teamId } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()

  const [team, setTeam] = useState(null)
  const [members, setMembers] = useState([])
  const [events, setEvents] = useState([])
  const [myRole, setMyRole] = useState(null)
  // Aggregate (one row) and per-member (list) stats from the views.
  const [teamAgg, setTeamAgg] = useState(null)
  const [memberStats, setMemberStats] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('roster')
  const [message, setMessage] = useState('')
  const [editingMember, setEditingMember] = useState(null)
  const [editTier, setEditTier] = useState('')
  const [editFieldRole, setEditFieldRole] = useState('')
  const [editDoe, setEditDoe] = useState('')
  const [showAddMember, setShowAddMember] = useState(false)
  const [newMemberEmail, setNewMemberEmail] = useState('')
  const [addingMember, setAddingMember] = useState(false)
  const [showAddEvent, setShowAddEvent] = useState(false)
  const [newEvent, setNewEvent] = useState({ event_name:'', event_date:'', location:'', faction:'', uniform:'', aar:'', status:'complete' })
  const [addingEvent, setAddingEvent] = useState(false)
  const [expandedEvent, setExpandedEvent] = useState(null)
  const isLead = myRole === 'lead'

  // ANU-113: auto-expand the active event when the list loads so GMs
  // don't have to scroll and click into it manually on go-live.
  // Respects any explicit selection the user has already made.
  useEffect(() => {
    if (expandedEvent !== null) return
    const active = events.find(e => e.status === 'active')
    if (active) setExpandedEvent(active.id)
  }, [events])
  const [showShareQR, setShowShareQR] = useState(false)
  const [showInvite, setShowInvite] = useState(false)

  useEffect(() => { setThemeColor('armor') }, [])

  useEffect(() => { fetchAll() }, [teamId, user])

  const fetchAll = async () => {
    if (!user || !teamId) return; setLoading(true)
    const [teamRes, membersRes, eventsRes, myRoleRes, aggRes, memberStatsRes] = await Promise.all([
      supabase.from('teams').select('*').eq('id', teamId).limit(1),
      supabase.from('team_members').select('*, user:users(id, display_name, callsign, email)').eq('team_id', teamId),
      supabase.from('team_events').select('*').eq('team_id', teamId).order('event_date', { ascending: false }),
      supabase.from('team_members').select('team_role').eq('team_id', teamId).eq('user_id', user.id).limit(1),
      // v_team_aggregate_stats — single row, lifetime team totals.
      supabase.from('v_team_aggregate_stats').select('*').eq('team_id', teamId).limit(1),
      // v_team_stats — one row per (member, event); we aggregate to per-member
      // lifetime numbers below.
      supabase.from('v_team_stats').select('user_id, kills_confirmed, kills_total, deaths, bounties_claimed, fragos_completed, total_points').eq('team_id', teamId),
    ])
    if (!teamRes.data?.[0]) { navigate('/teams'); return }
    setTeam(teamRes.data[0]); setMembers(membersRes.data || []); setEvents(eventsRes.data || []); setMyRole(myRoleRes.data?.[0]?.team_role || null)
    setTeamAgg(aggRes.data?.[0] || null)
    // Roll the per-event rows up to per-member totals so each operator card
    // can show a one-line stats line under their callsign.
    const perMember = new Map()
    ;(memberStatsRes.data || []).forEach(r => {
      const m = perMember.get(r.user_id) || { user_id: r.user_id, kills_confirmed: 0, kills_total: 0, deaths: 0, bounties_claimed: 0, fragos_completed: 0, total_points: 0 }
      m.kills_confirmed += r.kills_confirmed || 0
      m.kills_total += r.kills_total || 0
      m.deaths += r.deaths || 0
      m.bounties_claimed += r.bounties_claimed || 0
      m.fragos_completed += r.fragos_completed || 0
      m.total_points += r.total_points || 0
      perMember.set(r.user_id, m)
    })
    setMemberStats([...perMember.values()])
    setLoading(false)
  }

  const saveMemberEdit = async (memberId) => {
      const { error } = await supabase.rpc('update_team_member', {
        p_member_id: memberId,
        p_membership_tier: editTier || null,
        p_field_role: editFieldRole || null,
        p_doe: editDoe || null,
      })
      if (error) console.error('[CC] update_team_member failed:', error.message)
      setEditingMember(null); fetchAll()
    }

  const removeMember = async (memberId, isMe) => {
    if (!window.confirm(isMe ? 'Leave this team?' : 'Remove this member?')) return
    await supabase.from('team_members').delete().eq('id', memberId)
    if (isMe) navigate('/teams'); else fetchAll()
  }

  const addMember = async () => {
    const emailErr = validateEmail(newMemberEmail); if (emailErr) { setMessage(emailErr); return }
    setAddingMember(true)
    const { data: found } = await supabase.from('users').select('id').ilike('email', newMemberEmail.trim()).limit(1)
    if (!found?.[0]) { setMessage('No user found'); setAddingMember(false); return }
    const { error } = await supabase.from('team_members').insert({ team_id: teamId, user_id: found[0].id, team_role: 'member' })
    if (error) {
      setMessage(error.code === '23505'
        ? 'This operator is already on the team'
        : 'Failed: ' + error.message)
    }
    else { setMessage('Member added'); setNewMemberEmail(''); setShowAddMember(false); fetchAll() }
    setAddingMember(false)
  }

  const addEvent = async () => {
    const opErrors = validateLogOp(newEvent); if (Object.keys(opErrors).length > 0) { setMessage(Object.values(opErrors)[0]); return }
    setAddingEvent(true)
    const { error } = await supabase.from('team_events').insert({ team_id: teamId, created_by: user.id, ...newEvent, event_date: newEvent.event_date || null })
    if (error) setMessage('Failed: ' + error.message)
    else {
      setMessage('Event logged'); setNewEvent({ event_name:'', event_date:'', location:'', faction:'', uniform:'', aar:'', status:'complete' }); setShowAddEvent(false)
      if (newEvent.event_date) {
        const uninitiatedMembers = members.filter(m => !m.doe)
        if (uninitiatedMembers.length > 0) await Promise.all(uninitiatedMembers.map(m => supabase.from('team_members').update({ doe: newEvent.event_date }).eq('id', m.id)))
      }
      fetchAll()
    }
    setAddingEvent(false)
  }

  const deleteEvent = async (eventId) => { if (!window.confirm('Delete this event record?')) return; await supabase.from('team_events').delete().eq('id', eventId); fetchAll() }
  const tierStyle = (tier) => TIER_COLOR[tier] || { badge: '#5a6470', bg: 'rgba(90,100,112,.08)', text: '#5a6470' }

  if (loading) return (
    <div style={{ minHeight:'100vh', background:'#0e1116', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <span style={{ fontFamily:"'Orbitron', sans-serif", color:'#ff7a18', letterSpacing:'.12em', fontSize: 13 }}>LOADING COMMAND CENTER...</span>
    </div>
  )

  return (
    <div style={{ minHeight:'100vh', background:'radial-gradient(ellipse at 50% 10%, rgba(77,214,255,.08), transparent 60%), #0e1116', color:'#eaf2fb', fontFamily:"'Saira Condensed', 'Rajdhani', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700&family=Saira+Condensed:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');
        .cc-input { background: rgba(77,214,255,.04); border: 1px solid rgba(77,214,255,.25); color: #eaf2fb; padding: 6px 12px; font-family: 'Saira Condensed', sans-serif; font-size: 14px; width: 100%; outline: none; }
        .cc-input:focus { border-color: rgba(77,214,255,.6); }
        .cc-input::placeholder { color: #5a6470; }
        .cc-select { background: rgba(77,214,255,.04); border: 1px solid rgba(77,214,255,.25); color: #eaf2fb; padding: 6px 12px; font-family: 'Saira Condensed', sans-serif; font-size: 14px; outline: none; }
        .cc-select option { background: #0e1116; color: #eaf2fb; }
        .cc-btn { background: rgba(77,214,255,.08); border: 1px solid rgba(77,214,255,.25); color: #4dd6ff; padding: 6px 14px; font-family: 'Orbitron', sans-serif; font-weight: 700; font-size: 11px; cursor: pointer; letter-spacing: .08em; transition: all .2s; text-transform: uppercase; }
        .cc-btn:hover { background: rgba(77,214,255,.15); }
        .cc-btn-power { background: linear-gradient(135deg, #ff7a18, #d8500a); border: 1px solid #ff9040; color: #fff5e8; box-shadow: 0 0 12px rgba(255,122,24,.4); }
        .cc-btn-power:hover { box-shadow: 0 0 18px rgba(255,122,24,.6); }
        .cc-btn-red { background: rgba(255,65,54,.08); border: 1px solid rgba(255,65,54,.25); color: #ff4136; }
        .cc-btn-red:hover { background: rgba(255,65,54,.15); }
        .cc-row:hover { background: rgba(77,214,255,.03); }
        .cc-tab { padding: 8px 20px; font-family: 'Orbitron', sans-serif; font-size: 11px; letter-spacing: .12em; cursor: pointer; border-bottom: 2px solid transparent; color: #5a6470; transition: all .2s; text-transform: uppercase; background: none; border-top: none; border-left: none; border-right: none; }
        .cc-tab.active { color: #ff7a18; border-bottom-color: #ff7a18; }
        .cc-tab:hover { color: #ffb259; }
      `}</style>

      {/* Nav */}
      <div style={{ background:'linear-gradient(135deg, #2a2e36, #1a1d22)', borderBottom:'1px solid #4a5058', padding:'12px 24px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ display:'flex', alignItems:'center', gap:16 }}>
          <button onClick={() => {
            const params = new URLSearchParams(window.location.search)
            navigate(params.get('from') === 'player' ? '/play' : '/teams')
          }} style={{ background:'none', border:'none', color:'#5a6470', cursor:'pointer', display:'flex', alignItems:'center', gap:6, fontFamily:"'Saira Condensed', sans-serif", fontSize:14, letterSpacing:'.08em', textTransform:'uppercase' }}>
            <ArrowLeft size={14} /> TEAMS
          </button>
          <div style={{ width:1, height:20, background:'#4a5058' }} />
          <span style={{ fontFamily:"'Orbitron', sans-serif", fontSize:16, letterSpacing:'.12em', color:'#ff7a18', textTransform:'uppercase' }}>
            ◆ COMMAND CENTER
          </span>
        </div>
        <button onClick={fetchAll} style={{ background:'none', border:'none', color:'#5a6470', cursor:'pointer' }}><RefreshCw size={14} /></button>
      </div>

      {showShareQR && (
        <ShareQR event={null} factionId={null} onClose={() => setShowShareQR(false)} />
      )}

      {/* Team header */}
      <div style={{ background:'linear-gradient(135deg, #2a2e36, #1a1d22)', borderBottom:'1px solid #4a5058', padding:'20px 24px' }}>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
          <div>
            <h1 style={{ fontFamily:"'Orbitron', sans-serif", fontSize:28, letterSpacing:'.08em', color:'#ff7a18', margin:0, lineHeight:1, textTransform:'uppercase' }}>
              {team.name}
            </h1>
            {team.description && (
              <p style={{ color:'#9aa6b0', fontSize:14, marginTop:4, fontFamily:"'IBM Plex Mono', monospace" }}>{team.description}</p>
            )}
            <div style={{ display:'flex', gap:20, marginTop:8 }}>
              {team.founded_date && (
                <span style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize:12, color:'#5a6470' }}>
                  EST. {new Date(team.founded_date).getFullYear()}
                </span>
              )}
              <span style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize:12, color:'#5a6470' }}>{members.length} OPERATORS</span>
              <span style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize:12, color:'#5a6470' }}>{events.length} OPS LOGGED</span>
            </div>
          </div>
          <span style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize:11, color: isLead ? '#ff7a18' : '#5a6470' }}>
            {isLead ? '[ LEAD ]' : '[ OPERATOR ]'}
          </span>
        </div>
      </div>

      {/* Team aggregate stats — pulled from v_team_aggregate_stats. Hidden
          when the team has never participated in a Death-Card event yet. */}
      {teamAgg && (teamAgg.events_played || 0) > 0 && (
        <div style={{ background:'linear-gradient(180deg, rgba(77,214,255,.04), transparent)', borderBottom:'1px solid #2a2e36', padding:'14px 24px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(6, 1fr)', gap:12, maxWidth:900, margin:'0 auto' }}>
            {[
              { label: 'PTS',     val: teamAgg.team_total_points || 0,      color: '#ff7a18' },
              { label: 'KILLS',   val: teamAgg.team_kills_total || 0,       color: '#eaf2fb' },
              { label: 'CONF',    val: teamAgg.team_kills_confirmed || 0,   color: '#4dd6ff' },
              { label: 'DEATHS',  val: teamAgg.team_deaths || 0,            color: '#9aa6b0' },
              { label: 'KD',      val: teamAgg.kd_ratio != null ? Number(teamAgg.kd_ratio).toFixed(1) : '—', color: '#ffb259' },
              { label: 'EVENTS',  val: teamAgg.events_played || 0,          color: '#4dd6ff' },
            ].map(s => (
              <div key={s.label} style={{ textAlign:'center' }}>
                <p style={{ fontFamily:"'Orbitron', sans-serif", fontSize:20, color: s.color, lineHeight:1, fontVariantNumeric:'tabular-nums' }}>{s.val}</p>
                <p style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize:9, color:'#5a6470', letterSpacing:'.14em', marginTop:4 }}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {message && (
        <div style={{ background:'rgba(77,214,255,.06)', borderBottom:'1px solid rgba(77,214,255,.15)', padding:'8px 24px', fontFamily:"'IBM Plex Mono', monospace", fontSize:13, color:'#4dd6ff' }}>
          {message}
        </div>
      )}

      {/* Tabs */}
      <div style={{ background:'linear-gradient(135deg, #2a2e36, #1a1d22)', borderBottom:'1px solid #4a5058', padding:'0 24px', display:'flex', gap:4 }}>
        {['roster','ops log'].map(tab => (
          <button key={tab} className={`cc-tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
            {tab.toUpperCase()}
          </button>
        ))}
      </div>

      <div style={{ maxWidth:900, margin:'0 auto', padding:'24px' }}>

        {/* ── ROSTER ── */}
        {activeTab === 'roster' && (
          <div>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
              <span style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize:12, color:'#5a6470', letterSpacing:'.1em' }}>OPERATORS — {members.length}</span>
              {isLead && <button className="cc-btn" onClick={() => setShowAddMember(!showAddMember)} style={{ display:'flex', alignItems:'center', gap:6 }}><Plus size={12} /> ADD OPERATOR</button>}
            </div>

            {showAddMember && (
              <div style={{ background:'rgba(77,214,255,.04)', border:'1px solid rgba(77,214,255,.2)', padding:16, marginBottom:16, display:'flex', gap:8 }}>
                <input className="cc-input" type="email" value={newMemberEmail} onChange={e => setNewMemberEmail(e.target.value)} placeholder="operator email address" maxLength={100} />
                {isLead && <button className="cc-btn" onClick={() => setShowInvite(true)} style={{ display:'flex', alignItems:'center', gap:6 }}><Users size={12} /> INVITE QR</button>}
                <button className="cc-btn" onClick={addMember} disabled={addingMember}>ADD</button>
                <button className="cc-btn cc-btn-red" onClick={() => setShowAddMember(false)}>CANCEL</button>
              </div>
            )}

            {showInvite && (
              <InviteOperator
                teamId={teamId}
                teamName={team?.name}
                userId={user.id}
                onClose={() => { setShowInvite(false); fetchAll() }}
              />
            )}

            <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
              {members.map(m => {
                const isMe = m.user_id === user.id
                const cs = m.user?.callsign || m.user?.display_name || m.user?.email
                const tier = m.membership_tier
                const tc = tierStyle(tier)
                const isEditing = editingMember === m.id
                const ms = memberStats.find(s => s.user_id === m.user_id)

                return (
                  <div key={m.id} className="cc-row" style={{ background: tier ? tc.bg : 'rgba(255,255,255,.02)', border:'1px solid #4a5058', padding:'10px 14px' }}>
                    {isEditing ? (
                      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                          <select className="cc-select" value={editTier} onChange={e => setEditTier(e.target.value)}>
                            <option value="">— Tier —</option>
                            {DEFAULT_TIERS.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                          <select className="cc-select" value={editFieldRole} onChange={e => setEditFieldRole(e.target.value)}>
                            <option value="">— Field Role —</option>
                            {FIELD_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </div>
                        <div style={{ display:'flex', gap:8 }}>
                          <button className="cc-btn" onClick={() => saveMemberEdit(m.id)}>SAVE</button>
                          <button className="cc-btn cc-btn-red" onClick={() => setEditingMember(null)}>CANCEL</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                        {/* Row 1: Name + meta badges */}
                        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', minWidth: 0 }}>
                          {/* ANU-84 — overflowWrap so long callsigns wrap to
                              the next line on narrow viewports instead of
                              pushing the badges off the row. */}
                          <span style={{ fontFamily:"'Orbitron', sans-serif", fontSize:14, letterSpacing:'.05em', color: tier ? tc.text : '#9aa6b0', overflowWrap: 'anywhere', minWidth: 0 }}>
                            {cs}
                            {isMe && <span style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize:10, color:'#5a6470', marginLeft:8 }}>[YOU]</span>}
                          </span>
                          {tier && (
                            <span style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize:11, color: tc.badge, border:`1px solid ${tc.badge}44`, padding:'1px 8px', opacity:.9, flexShrink:0 }}>{tier.toUpperCase()}</span>
                          )}
                          {m.field_role && (
                            <span style={{ fontFamily:"'Saira Condensed', sans-serif", fontSize:12, color:'#9aa6b0', flexShrink:0 }}>{ROLE_ICON[m.field_role] || '·'} {m.field_role}</span>
                          )}
                          {m.doe && (
                            <span style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize:11, color:'#5a6470', flexShrink:0 }}>{new Date(m.doe).getFullYear()}</span>
                          )}
                          {m.team_role === 'lead' && (
                            <span style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize:10, color:'#ff7a18', border:'1px solid rgba(255,122,24,.4)', padding:'1px 6px', flexShrink:0 }}>LEAD</span>
                          )}
                        </div>
                        {/* Row 1.5: Per-member stats from v_team_stats. Only
                            rendered when the operator has at least one
                            recorded event under this team — keeps idle rows
                            uncluttered. */}
                        {ms && ms.total_points > 0 && (
                          <div style={{ display:'flex', gap:14, flexWrap:'wrap', fontFamily:"'IBM Plex Mono', monospace", fontSize:11, color:'#5a6470' }}>
                            <span>{ms.total_points} <span style={{ color:'#3a4048' }}>pts</span></span>
                            <span>{ms.kills_total} <span style={{ color:'#3a4048' }}>kills</span></span>
                            <span>{ms.kills_confirmed} <span style={{ color:'#3a4048' }}>conf</span></span>
                            <span>{ms.deaths} <span style={{ color:'#3a4048' }}>deaths</span></span>
                            <span>{ms.bounties_claimed} <span style={{ color:'#3a4048' }}>claimed</span></span>
                            <span>{ms.fragos_completed} <span style={{ color:'#3a4048' }}>fragos</span></span>
                          </div>
                        )}
                        {/* Row 2: Action buttons */}
                        {isLead && (
                          <div style={{ display:'flex', gap:8 }}>
                            <button className="cc-btn cc-btn-power" onClick={() => { setEditingMember(m.id); setEditTier(m.membership_tier||''); setEditFieldRole(m.field_role||''); setEditDoe(m.doe||'') }} style={{ padding:'4px 10px' }}>EDIT</button>
                            <button className="cc-btn cc-btn-red" onClick={() => removeMember(m.id, isMe)} style={{ padding:'4px 10px' }}>{isMe ? 'LEAVE' : 'REMOVE'}</button>
                          </div>
                        )}
                        {!isLead && isMe && (
                          <button className="cc-btn cc-btn-red" onClick={() => removeMember(m.id, true)} style={{ padding:'4px 10px', alignSelf:'flex-start' }}>LEAVE</button>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* ── OPS LOG ── */}
        {activeTab === 'ops log' && (
          <div>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
              <span style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize:12, color:'#5a6470', letterSpacing:'.1em' }}>OPERATION HISTORY — {events.length}</span>
              {isLead && <button className="cc-btn" onClick={() => setShowAddEvent(!showAddEvent)} style={{ display:'flex', alignItems:'center', gap:6 }}><Plus size={12} /> LOG OP</button>}
            </div>

            {showAddEvent && (
              <div style={{ background:'rgba(77,214,255,.04)', border:'1px solid rgba(77,214,255,.2)', padding:16, marginBottom:16 }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
                  <input className="cc-input" placeholder="Operation name *" value={newEvent.event_name} maxLength={60} onChange={e => setNewEvent({...newEvent, event_name: e.target.value})} />
                  <input className="cc-input" type="date" value={newEvent.event_date} onChange={e => setNewEvent({...newEvent, event_date: e.target.value})} />
                  <input className="cc-input" placeholder="Location / AO" value={newEvent.location} maxLength={80} onChange={e => setNewEvent({...newEvent, location: e.target.value})} />
                  <input className="cc-input" placeholder="Faction" value={newEvent.faction} maxLength={40} onChange={e => setNewEvent({...newEvent, faction: e.target.value})} />
                  <input className="cc-input" placeholder="Uniform / Camo" value={newEvent.uniform} maxLength={40} onChange={e => setNewEvent({...newEvent, uniform: e.target.value})} />
                  <select className="cc-select" value={newEvent.status} onChange={e => setNewEvent({...newEvent, status: e.target.value})} style={{ width:'100%' }}>
                    {EVENT_STATUSES.map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}
                  </select>
                </div>
                <textarea className="cc-input" placeholder="AAR — After Action Review (optional)" value={newEvent.aar} maxLength={1000} onChange={e => setNewEvent({...newEvent, aar: e.target.value})} rows={3} style={{ marginBottom:8, resize:'vertical' }} />
                <div style={{ display:'flex', gap:8 }}>
                  <button className="cc-btn cc-btn-power" onClick={addEvent} disabled={addingEvent}>LOG OP</button>
                  <button className="cc-btn cc-btn-red" onClick={() => setShowAddEvent(false)}>CANCEL</button>
                </div>
              </div>
            )}

            {events.length === 0 ? (
              <div style={{ textAlign:'center', padding:'48px 0', color:'#5a6470' }}>
                <p style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize:13 }}>NO OPS LOGGED</p>
                <p style={{ fontFamily:"'Saira Condensed', sans-serif", fontSize:12, marginTop:4 }}>Lead can log operations and AARs above</p>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
                {events.map(ev => {
                  const sc = STATUS_COLOR[ev.status] || '#9aa6b0'
                  const isExpanded = expandedEvent === ev.id
                  return (
                    <div key={ev.id} style={{ background:'rgba(255,255,255,.02)', border:'1px solid #4a5058', overflow:'hidden' }}>
                      <button onClick={() => setExpandedEvent(isExpanded ? null : ev.id)} style={{ width:'100%', background:'none', border:'none', padding:'10px 14px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between', color:'#eaf2fb' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:12, textAlign:'left' }}>
                          <span style={{ fontFamily:"'Orbitron', sans-serif", fontSize:13, letterSpacing:'.05em', color:'#eaf2fb' }}>{ev.event_name}</span>
                          {ev.event_date && <span style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize:11, color:'#5a6470' }}>{new Date(ev.event_date + 'T00:00:00').toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric' })}</span>}
                          {ev.location && <span style={{ fontFamily:"'Saira Condensed', sans-serif", fontSize:12, color:'#9aa6b0' }}>{ev.location}</span>}
                          <span style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize:10, color: sc, border:`1px solid ${sc}44`, padding:'1px 6px' }}>{(ev.status||'complete').toUpperCase()}</span>
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          {ev.aar && <span style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize:10, color:'#5a6470' }}>AAR</span>}
                          {isExpanded ? <ChevronDown size={14} color="#5a6470" /> : <ChevronRight size={14} color="#5a6470" />}
                        </div>
                      </button>
                      {isExpanded && (
                        <div style={{ borderTop:'1px solid #4a5058', padding:'12px 14px' }}>
                          <div style={{ display:'flex', gap:20, marginBottom: ev.aar ? 12 : 0, flexWrap:'wrap' }}>
                            {ev.faction && <span style={{ fontFamily:"'Saira Condensed', sans-serif", fontSize:13, color:'#9aa6b0' }}>FACTION: <span style={{ color:'#eaf2fb' }}>{ev.faction}</span></span>}
                            {ev.uniform && <span style={{ fontFamily:"'Saira Condensed', sans-serif", fontSize:13, color:'#9aa6b0' }}>UNIFORM: <span style={{ color:'#eaf2fb' }}>{ev.uniform}</span></span>}
                          </div>
                          {ev.aar && (
                            <div style={{ background:'rgba(77,214,255,.04)', border:'1px solid rgba(77,214,255,.15)', padding:'10px 12px' }}>
                              <p style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize:10, color:'#5a6470', letterSpacing:'.1em', marginBottom:6 }}>AAR</p>
                              <p style={{ fontFamily:"'Saira Condensed', sans-serif", fontSize:14, color:'#eaf2fb', lineHeight:1.5 }}>{ev.aar}</p>
                            </div>
                          )}
                          {isLead && (
                            <div style={{ marginTop:10, display:'flex', justifyContent:'flex-end' }}>
                              <button className="cc-btn cc-btn-red" onClick={() => deleteEvent(ev.id)} style={{ padding:'4px 10px', fontSize:10 }}>DELETE</button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
